import { DatabaseIdTypes } from "../../../Enums/ChoiceEnums"

export enum PatientFlagType {
    Short = 0,
    Icon = 1
}

export interface IPatientFlagBasicSettingsBase {
    shortDescription: string,
    longDescription: string,
    tooltip: string,
    colorId: PatientFlagColor,
    iconId?: PatientFlagIcon,
    flagStyleIcon: boolean,
    active: boolean,
    patientActionKeyGuid?: string
}

export interface IPatientFlagDisplaySettings extends IPatientFlagBasicSettingsBase {
    flagSetupId: number,
    emrPortalActive: boolean,
    billingPortalActive: boolean,
    schedulerPortalActive: boolean,
    documentPortalActive: boolean
}


export interface IPatientFlagSetupItem extends IPatientFlagDisplaySettings {
    flagCount: number   // The number of patents with this flag assigned
}

export interface IPatientFlagSaveSetupItem {
    flagSetupId?: number,
    shortDescription?: string,
    longDescription: string,
    tooltip: string,
    colorId: PatientFlagColor,
    iconId?: PatientFlagIcon,
    flagStyleIcon: boolean,
    active: boolean,
    emrPortalActive: boolean,
    billingPortalActive: boolean,
    schedulerPortalActive: boolean,
    documentPortalActive: boolean
}

export interface IPatientFlagSetupFlagItem {
    flagSetupId: number,
    description?: string
}

export interface IPatientFlagSaveSetupItemResult {
    flagSetupId?: number,
    errorMessage?: string
}

export interface IPatientFlagPatientAssignItem extends IPatientFlagDisplaySettings {
    patientAssigned: boolean
}

export interface IPatientFlagPatientAssignSaveItem {
    flagSetupId: number,
    active: boolean
}
export interface IPatientFlagPatientAssignSave {
    idType: DatabaseIdTypes,
    identifier: number,
    flagSetupList: IPatientFlagPatientAssignSaveItem[]
}

export interface IPatientFlagDisplayOrder {
    flagSetupId: number, 
    displayOrder?: number
}

export enum PatientFlagIcon {
    Missing = 0,
    HeartSolid = 1,
    HeartOutline = 2,
    Eye = 3,
    Flask = 4,
    Stethoscope = 5,
    Virus = 6,
    WheelChair = 7,
    Notes = 8,
    Vial = 9,
    Brain = 10,
    Accessible = 11,
    Hospital = 12,
    Tablets = 13,
    Radiation = 14,
    Lungs = 15,
    HeartCircleBolt = 16,
    MaskFace = 17,
    Capsules = 18,
    MortarPestle = 19,
    DNA = 20,
    Biohazard = 21,
    Clipboard = 22,
    Microscope = 23,
    Folder = 24,
    Prescription = 25,
    CreditCard = 26,
    DollarSign = 27,
    Phone = 28,
    Envelope = 29,
    Comment = 30,
    Mobile = 31,
    Paperclip = 32,
    Bookmark = 32,
    Users = 33,
    Location = 34,
    Bell = 35,
    CircleSolid = 36,
    CircleOutline = 37,
    CirclePlus = 38,
    CircleMinus = 39,
    SquareSolid = 40,
    SquareOutline = 41
}

export interface PatientFlagIconItem {
    id: PatientFlagIcon;
    class: string;
    label: string;
}

export const iconFlagList: PatientFlagIconItem[] = [
    { id: PatientFlagIcon.Accessible, class: "fa-solid fa-accessible-icon", label: "Accessible"},
    { id: PatientFlagIcon.Bell, class: "fa-solid fa-bell", label: "Bell"},
    { id: PatientFlagIcon.Biohazard, class: "fa-solid fa-biohazard", label: "Biohazard"},
    { id: PatientFlagIcon.Bookmark, class: "fa-solid fa-bookmark", label: "Bookmark"},
    { id: PatientFlagIcon.Brain, class: "fa-solid fa-brain", label: "Brain"},
    { id: PatientFlagIcon.Capsules, class: "fa-solid fa-capsules", label: "Capsules"},
    { id: PatientFlagIcon.Clipboard, class: "fa-solid fa-clipboard-list", label: "Clipboard"},
    { id: PatientFlagIcon.CircleMinus, class: "fa-solid fa-circle-minus", label: "Circle Minus"},
    { id: PatientFlagIcon.CircleOutline, class: "fa-regular fa-circle", label: "Circle Outline"},
    { id: PatientFlagIcon.CirclePlus, class: "fa-solid fa-circle-plus", label: "Cicle Plus"},
    { id: PatientFlagIcon.CircleSolid, class: "fa-solid fa-circle", label: "Circle Solid"},
    { id: PatientFlagIcon.Comment, class: "fa-solid fa-comment-medical", label: "Comment"},
    { id: PatientFlagIcon.CreditCard, class: "fa-regular fa-credit-card", label: "Credit Card"},
    { id: PatientFlagIcon.DNA, class: "fa-solid fa-dna", label: "DNA"},
    { id: PatientFlagIcon.DollarSign, class: "fa-solid fa-dollar-sign", label: "Dollar Sign"},
    { id: PatientFlagIcon.Envelope, class: "fa-solid fa-envelope", label: "Envelope"},
    { id: PatientFlagIcon.Eye, class: "fa-regular fa-eye", label: "Eye"},
    { id: PatientFlagIcon.Notes, class: "fa-solid fa-file-medical", label: "File"},
    { id: PatientFlagIcon.Flask, class: "fa-solid fa-flask", label: "Flask"},
    { id: PatientFlagIcon.Folder, class: "fa-solid fa-folder-plus", label: "Folder"},
    { id: PatientFlagIcon.HeartCircleBolt, class: "fa-solid fa-heart-circle-bolt", label: "Heart Bolt"},
    { id: PatientFlagIcon.HeartOutline, class: "fa-regular fa-heart", label: "Heart Outline"},
    { id: PatientFlagIcon.HeartSolid, class: "fa-solid fa-heart", label: "Heart Solid"},
    { id: PatientFlagIcon.Hospital, class: "fa-solid fa-hospital", label: "Hospital"},
    { id: PatientFlagIcon.Location, class: "fa-solid fa-location-dot", label: "Location"},
    { id: PatientFlagIcon.Lungs, class: "fa-solid fa-lungs", label: "Lungs"},
    { id: PatientFlagIcon.MaskFace, class: "fa-solid fa-mask-face", label: "Mask"},
    { id: PatientFlagIcon.Microscope, class: "fa-solid fa-microscope", label: "Microscope"},
    { id: PatientFlagIcon.Mobile, class: "fa-solid fa-mobile-screen-button", label: "Mobile"},
    { id: PatientFlagIcon.MortarPestle, class: "fa-solid fa-mortar-pestle", label: "Mortar Pestle"},
    { id: PatientFlagIcon.Paperclip, class: "fa-solid fa-paperclip", label: "Paper Clip"},
    { id: PatientFlagIcon.Phone, class: "fa-solid fa-phone", label: "Phone"},
    { id: PatientFlagIcon.Prescription, class: "fa-solid fa-prescription", label: "Prescription"},
    { id: PatientFlagIcon.Radiation, class: "fa-solid fa-radiation", label: "Radiation"},
    { id: PatientFlagIcon.SquareOutline, class: "fa-regular fa-square", label: "Square"},
    { id: PatientFlagIcon.SquareSolid, class: "fa-solid fa-square", label: "Square Solid"},
    { id: PatientFlagIcon.Flask, class: "fa-solid fa-stethoscope", label: "Stethoscope"},
    { id: PatientFlagIcon.Tablets, class: "fa-solid fa-tablets", label: "Tablets"},
    { id: PatientFlagIcon.Users, class: "fa-solid fa-users", label: "Users"},
    { id: PatientFlagIcon.Vial, class: "fa-solid fa-vial", label: "Vial"},
    { id: PatientFlagIcon.Virus, class: "fa-solid fa-virus", label: "Virus"},
    { id: PatientFlagIcon.WheelChair, class: "fa-solid fa-wheelchair", label: "Wheelchar"},
];

export enum PatientFlagColor {
    Missing = 0,
    Red1 = 1,
    Red2 = 2,
    Red3 = 3,
    Red4 = 4,
    Yellow = 5,
    Orange = 6,
    Gray = 7,
    Green1 = 8,
    Green2 = 9,
    Green3 = 10,
    Blue = 11,
    Purple1 = 12,
    Purple2 = 13,
    Pink1 = 14,
    Pink2 = 15,
    Pink3 = 16,
    Sea1 = 17,
    Sea2 = 18,
    Sea3 = 19,
}

export interface PatientFlagColorItem {
    id: PatientFlagColor,
    class: string;
}

export const colorFlagList: PatientFlagColorItem[] = [
    { id: PatientFlagColor.Red1, class: "patientFlagRed1" },
    { id: PatientFlagColor.Red2, class: "patientFlagRed2" },
    { id: PatientFlagColor.Red3, class: "patientFlagRed3" },
    { id: PatientFlagColor.Red4, class: "patientFlagRed4" },
    { id: PatientFlagColor.Yellow, class: "patientFlagYellow" },
    { id: PatientFlagColor.Orange, class: "patientFlagOrange" },
    { id: PatientFlagColor.Gray, class: "patientFlagGray" },
    { id: PatientFlagColor.Green1, class: "patientFlagGreen1" },
    { id: PatientFlagColor.Green2, class: "patientFlagGreen2" },
    { id: PatientFlagColor.Green3, class: "patientFlagGreen3" },
    { id: PatientFlagColor.Blue, class: "patientFlagBlue" },
    { id: PatientFlagColor.Purple1, class: "patientFlagPurple1" },
    { id: PatientFlagColor.Purple2, class: "patientFlagPurple2" },
    { id: PatientFlagColor.Pink1, class: "patientFlagPink1" },
    { id: PatientFlagColor.Pink2, class: "patientFlagPink2" },
    { id: PatientFlagColor.Pink3, class: "patientFlagPink3" },
    { id: PatientFlagColor.Sea1, class: "patientFlagSea1" },
    { id: PatientFlagColor.Sea2, class: "patientFlagSea2" },
    { id: PatientFlagColor.Sea3, class: "patientFlagSea3" },
];

export function GetFlagColorItem(colorId: PatientFlagColor) {
    let index = colorFlagList.findIndex(o => o.id == colorId);
    if (index < 0)
        return { id: PatientFlagColor.Missing, class: "" };
    return colorFlagList[index];
}
export function GetFlagIconItem(flagIconId: PatientFlagIcon)  {
    let index = iconFlagList.findIndex(o => o.id == flagIconId);
    if (index < 0)
        return { id: PatientFlagIcon.Missing, class: ""};
    return iconFlagList[index];
}
export function GetColorClass(colorId: PatientFlagColor) {
    const item = GetFlagColorItem(colorId);
    return `${item.class}`;
}
export function GetIconClass(iconId: PatientFlagIcon, colorId: PatientFlagColor) {
    const iconItem = GetFlagIconItem(iconId);
    const colorItem = GetFlagColorItem(colorId);
    return `${iconItem.class} ${colorItem.class} patientFlagIconPadding`;
}